import React, { useState, useCallback } from 'react';
import MetricSummary from './MetricSummary';
import MetricDrilldown from './MetricDrilldown';

/*
 * MetricExplorer — SUMMARY FIRST (P140 PR B).
 *
 * Owns the one piece of state the two levels share: which group, if any, is
 * open. Level 1 is a grouped roll-up with no contact names; level 2 is a paged,
 * 25-row list where names appear for the first time.
 *
 * ⚠ The level is derived from `group` rather than held as a separate flag. Two
 * pieces of state that must agree ("which level" and "which group") are two
 * pieces of state that can disagree — showing the detail with no group, which
 * would quietly widen the query back to everything.
 */
export default function MetricExplorer({ metricKey, title, onClose, onOpenContact, canExport, channels, columns, initialGroup = null, filters = null }) {
  /*
   * ⚠ A PRE-SET GROUP. The rep report opens already scoped to one rep, so the
   * explorer starts at the ROW LIST rather than at a summary whose only group
   * is the rep we already chose. `initialGroup` is the scope, not a default —
   * Back closes the report rather than returning to a one-row summary.
   */
  const [group, setGroup] = useState(initialGroup);
  /*
   * ⛔⛔ WITH A PRE-SET SCOPE, BACK CLOSES — it does NOT clear the group.
   * Clearing it would widen "202 overdue follow-ups — Ana Ituarte" into the
   * summary of ALL 1,082, under the same heading, one keystroke after the user
   * asked for one rep. That is P160 item 2's defect coming back in through the
   * Back button, and it looks exactly like a working screen.
   */
  const back = useCallback(() => {
    if (initialGroup) { if (typeof onClose === 'function') onClose(); return; }
    setGroup(null);
  }, [initialGroup, onClose]);

  /*
   * ⛔ NEVER "— undefined". A caller that scopes by key alone has no label to
   * render, and P154/P160 item 5 are both this same defect: a storage value, or
   * nothing at all, used as a heading. No label ⇒ no suffix.
   */
  const heading = group && group.label ? `${title} — ${group.label}` : title;

  if (!group) {
    return (
      <MetricSummary
        metricKey={metricKey}
        title={title}
        onClose={onClose}
        onOpenGroup={setGroup}
        channels={channels}
        filters={filters}
      />
    );
  }

  return (
    <MetricDrilldown
      metricKey={metricKey}
      title={heading}
      group={group}
      onBack={back}
      onClose={onClose}
      onOpenContact={onOpenContact}
      canExport={canExport}
      channels={channels}
      columns={columns}
      filters={filters}
    />
  );
}
